import { useState, useRef, useEffect } from "react";
import { Share2, Twitter, Linkedin, Copy, Check, X } from "lucide-react";

/**
 * ShareMenu — A reusable neo-brutalist share dropdown that works everywhere.
 * 
 * Props:
 *   url       — the link to share
 *   title     — content title for the share text
 *   text      — optional custom text (falls back to a generated one)
 *   compact   — if true, uses a smaller button (for card footers)
 *   count     — optional share count to display next to the icon
 *   onShare   — optional callback when a share action occurs (to update DB count)
 *   className — optional extra classes on the trigger button
 */
export default function ShareMenu({ url, title, text, compact = false, count, onShare, className = "" }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const ref = useRef(null);

  // Close menu on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // Reset copied state
  useEffect(() => {
    if (copied) {
      const t = setTimeout(() => setCopied(false), 1500);
      return () => clearTimeout(t);
    }
  }, [copied]);

  const shareText = text || `Check out "${title}" on #NoJobNoVC — funding businesses with grit, not VCs. 🚀`;
  const fullUrl = url.startsWith("http") ? url : `${window.location.origin}${url}`;

  const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(fullUrl)}`;
  const linkedinUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(fullUrl)}`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(fullUrl);
      setCopied(true);
    } catch {
      // Fallback for older browsers
      const ta = document.createElement("textarea");
      ta.value = fullUrl;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      setCopied(true);
    }
    onShare?.();
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title, text: shareText, url: fullUrl });
        setOpen(false);
        onShare?.();
      } catch { /* user cancelled */ }
    }
  };

  return (
    <div className="relative" ref={ref}>
      {/* Trigger Button */}
      <button
        onClick={() => setOpen(!open)}
        className={`brutal-btn brutal-btn-yellow ${compact ? "text-xs" : "text-sm"} ${className}`}
        data-testid="share-trigger"
      >
        <Share2 size={compact ? 14 : 16} />
        {count != null && <span>{count}</span>}
        {!compact && !count && " Share"}
      </button>

      {/* Dropdown Menu */}
      {open && (
        <div
          className="absolute right-0 top-full mt-2 z-50 w-56 border-2 border-ink bg-cream shadow-brutal-sm"
          style={{ background: "#F4F1EA" }}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-3 py-2 border-b-2 border-ink">
            <span className="font-display font-extrabold text-xs uppercase tracking-widest">Share</span>
            <button onClick={() => setOpen(false)} className="hover:text-marker-red">
              <X size={14} />
            </button>
          </div>

          {/* Options */}
          <div className="p-2 space-y-1">
            {/* Twitter / X */}
            <a
              href={twitterUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => { setOpen(false); onShare?.(); }}
              className="flex items-center gap-3 px-3 py-2 font-mono text-xs uppercase tracking-widest hover:bg-marker-yellow border-2 border-transparent hover:border-ink transition-all"
              data-testid="share-twitter"
            >
              <Twitter size={16} />
              <span>Tweet it</span>
            </a>

            {/* LinkedIn */}
            <a
              href={linkedinUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => { setOpen(false); onShare?.(); }}
              className="flex items-center gap-3 px-3 py-2 font-mono text-xs uppercase tracking-widest hover:bg-marker-blue hover:text-white border-2 border-transparent hover:border-ink transition-all"
              data-testid="share-linkedin"
            >
              <Linkedin size={16} />
              <span>LinkedIn</span>
            </a>

            {/* Copy Link */}
            <button
              onClick={copyLink}
              className="flex items-center gap-3 px-3 py-2 font-mono text-xs uppercase tracking-widest hover:bg-ink hover:text-cream border-2 border-transparent hover:border-ink transition-all w-full text-left"
              data-testid="share-copy"
            >
              {copied ? <Check size={16} className="text-green-600" /> : <Copy size={16} />}
              <span>{copied ? "Copied!" : "Copy link"}</span>
            </button>

            {/* Native Share (mobile / macOS) */}
            {typeof navigator !== "undefined" && navigator.share && (
              <button
                onClick={nativeShare}
                className="flex items-center gap-3 px-3 py-2 font-mono text-xs uppercase tracking-widest hover:bg-marker-red hover:text-white border-2 border-transparent hover:border-ink transition-all w-full text-left"
                data-testid="share-native"
              >
                <Share2 size={16} />
                <span>More options…</span>
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
